import { debounce } from './utils/debounce.js';
/**
 * @module video-cover-resizer
 * Resizes iframes within specified containers to mimic CSS `object-fit: cover`.
 * This ensures the iframe content fills its container while maintaining aspect ratio,
 * clipping any overflow. For this to work visually, the container element
 * requires `position: relative; overflow: hidden;` and the iframe should have
 * `position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);`
 * in your stylesheet.
 */

let pendingRafId = null;

/**
 * Calculates and applies new dimensions to iframes to make them cover their containers.
 * @param {string} containerSelector - The CSS selector for the container elements (e.g., '.video-container').
 * @param {HTMLElement|Document} context - The context to query within. Defaults to document.
 */
export function resizeIframesToCover(containerSelector, context = document) {
    const containers = context.querySelectorAll(containerSelector);
    if (!containers.length) return;

    if (pendingRafId !== null) {
        cancelAnimationFrame(pendingRafId);
        pendingRafId = null;
    }

    // Pass 1: Read phase (Batch layout calculations without mutating DOM)
    const updates = [];

    containers.forEach(container => {
        const iframe = container.querySelector('iframe');
        if (!iframe) return;

        const iframeWidth = parseFloat(iframe.getAttribute('width'));
        const iframeHeight = parseFloat(iframe.getAttribute('height'));

        if (isNaN(iframeWidth) || isNaN(iframeHeight) || iframeWidth === 0 || iframeHeight === 0) {
            return;
        }

        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;

        if (containerHeight === 0) return;

        const iframeAspectRatio = iframeWidth / iframeHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let newWidth, newHeight;

        if (containerAspectRatio > iframeAspectRatio) {
            newWidth = containerWidth;
            newHeight = newWidth / iframeAspectRatio;
        } else {
            newHeight = containerHeight;
            newWidth = newHeight * iframeAspectRatio;
        }

        updates.push({ iframe, width: newWidth, height: newHeight });
    });

    // Pass 2: Write phase (Batch DOM mutations in a single frame)
    if (updates.length > 0) {
        pendingRafId = requestAnimationFrame(() => {
            updates.forEach(({ iframe, width, height }) => {
                iframe.style.width = `${width}px`;
                iframe.style.height = `${height}px`;
            });
            pendingRafId = null;
        });
    }
}

/**
 * Registers the global (persistent) resize/orientation listeners that keep
 * cover-fitted iframes sized to their containers.
 *
 * @param {string} containerSelector - CSS selector for the container elements.
 * @return {void}
 */
export function initializeVideoCoverResizerListeners(containerSelector = '.video-container') {
    const handleResize = debounce(() => resizeIframesToCover(containerSelector), 250);
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });
}